import { Bot, ChevronDown, History, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ConversationTab,
    KnowledgeBaseModal,
} from '../components/conversation';
import { BuyTokensModal, Card, InsufficientTokensModal, Tabs } from '../components/ui';
import { sanitizeUserMessageForDisplay } from '../constants/canvasRules';
import { useOrganization, usePage } from '../contexts';
import { conversationService, llmService } from '../services';
import {
  HistoryItemResponse,
  HistorySessionSummaryResponse,
} from '../services/ConversationService';
import {
    useConversationTabs,
    useCrews,
    useKnowledgeSources,
    useMessageSending,
    useConnectorSources,
    useTokens,
} from '../hooks';
import { KnowledgeSource } from '../types';
import { LlmCatalogModel } from '../types/entities';
import { ConnectorSource } from '../types/entities';
import {
  ConversationImageAttachment,
  DEFAULT_SEARCH_CONTEXT_TYPE,
  Message,
  SearchContextType,
} from '../types/conversation';

const Conversations: React.FC = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const { setPageInfo } = usePage();

  // Custom hooks
  const {
    conversationTabs,
    activeTabId,
    isInitialized,
    setIsInitialized,
    loadConversationsFromStorage,
    createNewConversationTab,
    selectCrewForActiveTab,
    closeConversationTab,
    updateTabMessages,
    updateTabKnowledgeSources,
    updateTabConnectorSources,
    updateTabSessionId,
    updateTabSearchContextType,
    updateTabUseLiveData,
    updateTabLlmModel,
    updateTabDraftMessage,
    updateTabUseCanvas,
    createConversationTabFromHistory,
    getActiveTab,
    setActiveTab,
    initializeTabs,
  } = useConversationTabs();

  const { crews, loadCrews } = useCrews(currentOrganization?.id);
  const {
    knowledgeSources,
    isLoadingKnowledge,
    loadKnowledgeSources,
    toggleKnowledgeSource,
  } = useKnowledgeSources(currentOrganization?.id);
  const {
    connectorSources,
    isLoadingConnectors,
    loadConnectorSources,
    toggleConnectorSource,
  } = useConnectorSources(currentOrganization?.id, { activeOnly: false });

  const {
    handleSendMessage,
    stopStreaming,
    isTabStreaming,
  } = useMessageSending();
  const { tokensBalance, isModalOpen, closeModal, isBuyTokensModalOpen, closeBuyTokensModal } = useTokens();

  // Local state
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
  const [historyConversations, setHistoryConversations] = useState<
    HistorySessionSummaryResponse[]
  >([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryMenuOpen, setIsHistoryMenuOpen] = useState(false);
  const [openingHistoryConversationId, setOpeningHistoryConversationId] =
    useState<string | null>(null);
  const [availableLlmModels, setAvailableLlmModels] = useState<LlmCatalogModel[]>(
    []
  );
  const [defaultLlmModel, setDefaultLlmModel] = useState<string | null>(null);
  const [isLoadingLlmModels, setIsLoadingLlmModels] = useState(false);
  const [llmModelsError, setLlmModelsError] = useState(false);
  const hasInitializedTabsRef = useRef(false);
  const hydratedSessionIdsRef = useRef<Set<string>>(new Set());

  // Effects
  useEffect(() => {
    setPageInfo(t('conversations.title'), t('conversations.subtitle'));
  }, [setPageInfo, t]);

  useEffect(() => {
    if (currentOrganization) {
      loadCrews();
      loadKnowledgeSources();
      loadConnectorSources();
    }
  }, [currentOrganization, loadCrews, loadKnowledgeSources, loadConnectorSources]);

  useEffect(() => {
    let isMounted = true;

    const loadHistoryConversations = async () => {
      if (!currentOrganization) return;

      setIsHistoryLoading(true);
      try {
        const conversations = await conversationService.listHistorySessions();
        if (!isMounted) return;

        const sorted = [...conversations].sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at || '').getTime() -
            new Date(a.updated_at || a.created_at || '').getTime()
        );
        setHistoryConversations(sorted);
      } catch (error) {
        if (isMounted) {
          setHistoryConversations([]);
        }
      } finally {
        if (isMounted) {
          setIsHistoryLoading(false);
        }
      }
    };

    loadHistoryConversations();

    return () => {
      isMounted = false;
    };
  }, [currentOrganization]);

  useEffect(() => {
    let isMounted = true;

    const loadLlmModels = async () => {
      if (!currentOrganization) {
        if (isMounted) {
          setAvailableLlmModels([]);
          setDefaultLlmModel(null);
          setLlmModelsError(false);
          setIsLoadingLlmModels(false);
        }
        return;
      }

      setIsLoadingLlmModels(true);
      setLlmModelsError(false);
      try {
        const response = await llmService.listAvailable();
        if (!isMounted) return;

        setAvailableLlmModels(response.models || []);
        setDefaultLlmModel(response.default_model || null);
      } catch (error) {
        if (!isMounted) return;
        setAvailableLlmModels([]);
        setDefaultLlmModel(null);
        setLlmModelsError(true);
      } finally {
        if (isMounted) {
          setIsLoadingLlmModels(false);
        }
      }
    };

    loadLlmModels();

    return () => {
      isMounted = false;
    };
  }, [currentOrganization]);

  // Load conversations from localStorage and create default tab if needed
  useEffect(() => {
    if (!isInitialized && currentOrganization && !hasInitializedTabsRef.current) {
      hasInitializedTabsRef.current = true;
      const { tabs, activeId } = loadConversationsFromStorage();

      if (tabs.length > 0) {
        initializeTabs(tabs, activeId);
      } else {
        createNewConversationTab(t('conversations.newConversation'));
      }

      setIsInitialized(true);
    }
  }, [
    isInitialized,
    currentOrganization,
    loadConversationsFromStorage,
    createNewConversationTab,
    initializeTabs,
    t,
    setIsInitialized,
  ]);

  // Handlers
  const handleCrewSelect = useCallback(
    (crewId: number) => {
      selectCrewForActiveTab(crewId, crews);
    },
    [selectCrewForActiveTab, crews]
  );

  const handleKnowledgeSourceToggle = useCallback(
    (source: KnowledgeSource) => {
      const activeTab = getActiveTab();
      if (!activeTab) return;

      toggleKnowledgeSource(
        source,
        activeTab.selectedKnowledgeSources,
        newSources => {
          updateTabKnowledgeSources(activeTab.id, newSources);
          if (
            newSources.length > 0 &&
            activeTab.searchContextType !== 'specific'
          ) {
            updateTabSearchContextType(activeTab.id, 'specific');
          }
        }
      );
    },
    [
      getActiveTab,
      toggleKnowledgeSource,
      updateTabKnowledgeSources,
      updateTabSearchContextType,
    ]
  );

  const openKnowledgeBaseModal = useCallback(() => {
    setShowKnowledgeBaseModal(true);
  }, []);

  const handleCloseKnowledgeBaseModal = useCallback(() => {
    const activeTab = getActiveTab();
    if (activeTab) {
      const noDocsSelected =
        activeTab.searchContextType === 'specific' &&
        activeTab.selectedKnowledgeSources.length === 0;
      const noConnectorsSelected =
        activeTab.searchContextType === 'connectors' &&
        (activeTab.selectedConnectorSources || []).length === 0;

      if (noDocsSelected || noConnectorsSelected) {
        updateTabSearchContextType(activeTab.id, DEFAULT_SEARCH_CONTEXT_TYPE);
      }
    }

    setShowKnowledgeBaseModal(false);
  }, [getActiveTab, updateTabSearchContextType]);

  const handleConnectorSourceToggle = useCallback(
    (source: ConnectorSource) => {
      const activeTab = getActiveTab();
      if (!activeTab) return;

      if (!source.is_active) {
        return;
      }

      toggleConnectorSource(
        source,
        activeTab.selectedConnectorSources || [],
        newSources => {
          updateTabConnectorSources(activeTab.id, newSources);
          if (
            newSources.length > 0 &&
            activeTab.searchContextType !== 'connectors'
          ) {
            updateTabSearchContextType(activeTab.id, 'connectors');
          }
        }
      );
    },
    [
      getActiveTab,
      toggleConnectorSource,
      updateTabConnectorSources,
      updateTabSearchContextType,
    ]
  );

  const handleSearchContextTypeChange = useCallback(
    (value: SearchContextType) => {
      const activeTab = getActiveTab();
      if (!activeTab) return;

      updateTabSearchContextType(activeTab.id, value);
    },
    [getActiveTab, updateTabSearchContextType]
  );

  const handleUseLiveDataChange = useCallback(
    (value: boolean) => {
      const activeTab = getActiveTab();
      if (!activeTab) return;

      updateTabUseLiveData(activeTab.id, value);
    },
    [getActiveTab, updateTabUseLiveData]
  );

  const handleDraftMessageChange = useCallback(
    (tabId: string, value: string) => {
      updateTabDraftMessage(tabId, value);
    },
    [updateTabDraftMessage]
  );

  const handleUseCanvasChange = useCallback(
    (tabId: string, value: boolean) => {
      updateTabUseCanvas(tabId, value);
    },
    [updateTabUseCanvas]
  );

  const handleSelectedLlmModelChange = useCallback(
    (value: string | null) => {
      const activeTab = getActiveTab();
      if (!activeTab) return;

      updateTabLlmModel(activeTab.id, value);
    },
    [getActiveTab, updateTabLlmModel]
  );

  const handleSendMessageByTab = useCallback(
    (tabId: string, attachments?: ConversationImageAttachment[]) => {
      const selectedTab = conversationTabs.find(tab => tab.id === tabId);
      if (!selectedTab) return;

      handleSendMessage(
        selectedTab,
        selectedTab.draftMessage || '',
        attachments || [],
        updateTabMessages,
        updateTabSessionId,
        currentTabId => updateTabDraftMessage(currentTabId, ''),
        (currentTabId, text) => updateTabDraftMessage(currentTabId, text),
        currentTabId =>
          conversationTabs.find(tab => tab.id === currentTabId)?.messages || []
      );
    },
    [
      conversationTabs,
      handleSendMessage,
      updateTabMessages,
      updateTabSessionId,
      updateTabDraftMessage,
    ]
  );

  const handleStopMessageByTab = useCallback(
    (tabId: string) => {
      stopStreaming(tabId);
    },
    [stopStreaming]
  );

  // Removed unused handleKeyPressWrapper

  const formatTime = useCallback((date: Date | string) => {
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return '--:--';
      }
      return dateObj.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return '--:--';
    }
  }, []);

  const looksLikeFullHtmlDocument = (value: string): boolean => {
    const content = value.trim().toLowerCase();
    if (!content) {
      return false;
    }
    if (content.startsWith('<!doctype html') || content.startsWith('<html')) {
      return true;
    }
    return content.includes('<html') && content.includes('</html>');
  };

  const buildMessageItem = (
    idPrefix: string,
    index: number,
    content: string,
    role: 'user' | 'assistant' | 'status',
    timestamp?: string,
    useCanvas?: boolean
  ): Message => {
    const normalizedContent =
      role === 'user' ? sanitizeUserMessageForDisplay(content) : content;
    return {
      id: `${idPrefix}-${index}`,
      role,
      content: normalizedContent,
      timestamp: timestamp ? new Date(timestamp) : new Date(),
      ...(typeof useCanvas === 'boolean' ? { useCanvas } : {}),
    };
  };

  const buildMessagesFromHistoryItem = useCallback(
    (historyItem: HistoryItemResponse): Message[] => {
      const messages: Message[] = [];

      if (historyItem.user_message?.trim()) {
        messages.push(
          buildMessageItem(
            `history-user-${historyItem.id}`,
            1,
            historyItem.user_message,
            'user',
            historyItem.created_at || undefined
          )
        );
      }

      if (historyItem.ai_response?.trim()) {
        const inferredUseCanvas =
          historyItem.use_canvas ??
          looksLikeFullHtmlDocument(historyItem.ai_response);
        messages.push(
          buildMessageItem(
            `history-assistant-${historyItem.id}`,
            1,
            historyItem.ai_response,
            'assistant',
            historyItem.updated_at || historyItem.created_at || undefined,
            inferredUseCanvas
          )
        );
      }

      return messages;
    },
    []
  );

  const buildMessagesFromHistorySession = useCallback(
    (historyItems: HistoryItemResponse[]): Message[] => {
      if (!Array.isArray(historyItems) || historyItems.length === 0) {
        return [];
      }

      const orderedItems = [...historyItems].sort((a, b) => {
        const aTime = new Date(a.created_at || a.updated_at || '').getTime();
        const bTime = new Date(b.created_at || b.updated_at || '').getTime();
        return aTime - bTime;
      });

      const messages: Message[] = [];
      orderedItems.forEach((historyItem, index) => {
        const itemMessages = buildMessagesFromHistoryItem(historyItem);
        itemMessages.forEach((msg, innerIndex) => {
          messages.push({
            ...msg,
            id: `history-session-${historyItem.id}-${index}-${innerIndex}`,
          });
        });
      });
      return messages;
    },
    [buildMessagesFromHistoryItem]
  );

  const mergeMessagesWithoutDuplicates = useCallback(
    (cachedMessages: Message[], sessionMessages: Message[]): Message[] => {
      const combined = [...(cachedMessages || []), ...(sessionMessages || [])];
      const seen = new Set<string>();
      const deduped: Message[] = [];

      combined.forEach(message => {
        const normalizedContent =
          message.role === 'user'
            ? sanitizeUserMessageForDisplay(message.content || '')
            : message.content;
        const timestamp = new Date(message.timestamp).getTime();
        const signature = `${message.role}|${Number.isNaN(timestamp) ? 'na' : timestamp}|${normalizedContent}`;
        if (seen.has(signature)) {
          return;
        }
        seen.add(signature);
        deduped.push({
          ...message,
          content: normalizedContent,
          timestamp:
            message.timestamp instanceof Date
              ? message.timestamp
              : new Date(message.timestamp),
        });
      });

      return deduped.sort((a, b) => {
        const aTime = new Date(a.timestamp).getTime();
        const bTime = new Date(b.timestamp).getTime();
        return aTime - bTime;
      });
    },
    []
  );

  const formatHistoryDate = useCallback((value: string) => {
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '';
    }
    return dateValue.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const getHistoryTitle = useCallback((conversation: HistorySessionSummaryResponse) => {
    const messagePreview = sanitizeUserMessageForDisplay(
      conversation.last_user_message || ''
    ).trim();
    if (!messagePreview) {
      return `Sessão ${conversation.session_id}`;
    }

    return messagePreview.length > 70
      ? `${messagePreview.substring(0, 70)}...`
      : messagePreview;
  }, []);

  const historyConversationsToShow = useMemo(
    () => historyConversations.slice(0, 30),
    [historyConversations]
  );

  const handleOpenHistoryConversation = useCallback(
    async (historySession: HistorySessionSummaryResponse) => {
      setOpeningHistoryConversationId(historySession.session_id);
      setIsHistoryMenuOpen(false);

      try {
        const historyConversations = await conversationService.getHistoryBySessionId(
          historySession.session_id
        );
        const historyMessages = buildMessagesFromHistorySession(historyConversations);

        const existingTab = conversationTabs.find(
          tab => String(tab.sessionId || '').trim() === historySession.session_id
        );
        if (existingTab) {
          const mergedMessages = mergeMessagesWithoutDuplicates(
            existingTab.messages || [],
            historyMessages
          );
          updateTabMessages(existingTab.id, mergedMessages);
          setActiveTab(existingTab.id);
          return;
        }

        const newTabId = createConversationTabFromHistory({
          historyConversationId: historySession.latest_history_id,
          tabName: getHistoryTitle(historySession),
          selectedCrewId: historySession.crew_id || null,
          sessionId: historySession.session_id || null,
        });

        if (historyMessages.length > 0) {
          updateTabMessages(newTabId, historyMessages);
        }
      } finally {
        setOpeningHistoryConversationId(null);
      }
    },
    [
      buildMessagesFromHistorySession,
      conversationTabs,
      createConversationTabFromHistory,
      getHistoryTitle,
      mergeMessagesWithoutDuplicates,
      setActiveTab,
      updateTabMessages,
    ]
  );

  // Stable signature of the tabs that actually need hydration. Using
  // `conversationTabs` directly in the deps array would re-run this effect
  // on every render (each streamed token recreates the messages array),
  // causing redundant GET /v1/history/session/<id> calls during execute-ai.
  const tabsHydrationKey = useMemo(
    () =>
      conversationTabs
        .map(tab => `${tab.id}:${String(tab.sessionId || '').trim()}`)
        .join('|'),
    [conversationTabs]
  );

  useEffect(() => {
    if (!isInitialized || !currentOrganization || conversationTabs.length === 0) {
      return;
    }

    let cancelled = false;
    const hydrateTabsFromHistory = async () => {
      const tabsToHydrate = conversationTabs.filter(tab => {
        const sessionId = String(tab.sessionId || '').trim();
        if (!sessionId) {
          return false;
        }
        // Skip tabs the user just created locally — they have no server-side
        // history yet and would always answer 404 until the first message
        // is persisted, generating a polling loop.
        if (tab.createdLocally) {
          return false;
        }
        return !hydratedSessionIdsRef.current.has(sessionId);
      });

      for (const tab of tabsToHydrate) {
        const sessionId = String(tab.sessionId || '').trim();
        if (!sessionId) {
          continue;
        }

        hydratedSessionIdsRef.current.add(sessionId);
        try {
          const historyItems = await conversationService.getHistoryBySessionId(sessionId);
          if (cancelled) {
            return;
          }
          const sessionMessages = buildMessagesFromHistorySession(historyItems);
          if (!sessionMessages.length) {
            continue;
          }
          const mergedMessages = mergeMessagesWithoutDuplicates(
            tab.messages || [],
            sessionMessages
          );
          updateTabMessages(tab.id, mergedMessages);
        } catch (error) {
          // 404 = sessão ainda não tem mensagens persistidas (esperado para
          // sessões novas). Mantemos no set para evitar re-fetch em loop.
          // Outros erros (5xx, rede) → liberar para retry no próximo render.
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (status !== 404) {
            hydratedSessionIdsRef.current.delete(sessionId);
          }
        }
      }
    };

    void hydrateTabsFromHistory();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildMessagesFromHistorySession,
    tabsHydrationKey,
    currentOrganization,
    isInitialized,
    mergeMessagesWithoutDuplicates,
    updateTabMessages,
  ]);

  const historyControl = (
    <div className="relative flex items-center h-full">
      <button
        type="button"
        onClick={() => setIsHistoryMenuOpen(prev => !prev)}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <History className="h-3.5 w-3.5" />
        Histórico
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {isHistoryMenuOpen && (
        <div className="absolute right-0 top-full mt-2 z-30 w-96 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          <div className="max-h-96 overflow-y-auto p-2">
            {isHistoryLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Carregando histórico...
              </div>
            ) : historyConversationsToShow.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Nenhuma conversa no histórico.
              </div>
            ) : (
              historyConversationsToShow.map(historyConversation => (
                <button
                  key={historyConversation.session_id}
                  type="button"
                  onClick={() => handleOpenHistoryConversation(historyConversation)}
                  disabled={
                    openingHistoryConversationId === historyConversation.session_id
                  }
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getHistoryTitle(historyConversation)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatHistoryDate(
                      historyConversation.updated_at ||
                        historyConversation.created_at ||
                        ''
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render
  return (
    <div className="h-full min-h-0">
      {/* Chat Container */}
      <div
        className="h-[calc(100vh-5.5rem)] flex flex-col overflow-hidden"
      >
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Chat Area with Tabs */}
          {conversationTabs.length > 0 ? (
            <Tabs
              tabs={conversationTabs.map(tab => ({
                id: tab.id,
                label: tab.name,
                closable: true,
                content: (
                  <ConversationTab
                    tab={tab}
                    crews={crews}
                    message={tab.draftMessage}
                    setMessage={value => handleDraftMessageChange(tab.id, value)}
                    onSendMessage={attachments =>
                      handleSendMessageByTab(tab.id, attachments)
                    }
                    onStopMessage={() => handleStopMessageByTab(tab.id)}
                    onCrewSelect={handleCrewSelect}
                    isStreaming={isTabStreaming(tab.id)}
                    formatTime={formatTime}
                    useCanvas={tab.useCanvas}
                    onUseCanvasChange={value =>
                      handleUseCanvasChange(tab.id, value)
                    }
                    onSearchContextTypeChange={handleSearchContextTypeChange}
                    onUseLiveDataChange={handleUseLiveDataChange}
                    onOpenKnowledgeBaseModal={openKnowledgeBaseModal}
                    llmModels={availableLlmModels}
                    llmDefaultModel={defaultLlmModel}
                    llmLoading={isLoadingLlmModels}
                    llmLoadError={llmModelsError}
                    onSelectedLlmModelChange={handleSelectedLlmModelChange}
                  />
                ),
              }))}
              activeTabId={activeTabId || ''}
              onTabChange={setActiveTab}
              onTabClose={closeConversationTab}
              onAddTab={() => createNewConversationTab()}
              headerRight={historyControl}
              className="h-full rounded-lg border border-light-200 dark:border-graphite-700"
            />
          ) : (
            <Card className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-gray-400">
                <Bot className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <h3 className="text-lg font-medium mb-2 text-gray-900 dark:text-white">
                  {t('conversations.noConversations')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('conversations.startConversation')}
                </p>
                <button
                  type="button"
                  onClick={() => createNewConversationTab(t('conversations.newConversation'))}
                  className="mt-4 inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  {t('conversations.newConversation')}
                </button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <KnowledgeBaseModal
        isOpen={showKnowledgeBaseModal}
        onClose={handleCloseKnowledgeBaseModal}
        knowledgeSources={knowledgeSources}
        selectedKnowledgeSources={
          getActiveTab()?.selectedKnowledgeSources || []
        }
        isLoadingKnowledge={isLoadingKnowledge}
        onToggleKnowledge={handleKnowledgeSourceToggle}
        connectorSources={connectorSources}
        selectedConnectorSources={getActiveTab()?.selectedConnectorSources || []}
        isLoadingConnectors={isLoadingConnectors}
        onToggleConnector={handleConnectorSourceToggle}
        searchContextType={
          getActiveTab()?.searchContextType || DEFAULT_SEARCH_CONTEXT_TYPE
        }
        onSearchContextTypeChange={handleSearchContextTypeChange}
      />

      {/* Insufficient Tokens Modal */}
      <InsufficientTokensModal
        isOpen={isModalOpen}
        onClose={closeModal}
        tokensBalance={tokensBalance}
      />

      {/* Buy Tokens Modal */}
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />

    </div>
  );
};

export default Conversations;
